// Ambassador: Yatharth.
//
import { Box } from '@mui/material';
import GoogleMapReact from 'google-map-react';
import { useEffect, useMemo, useState } from 'react';

import { useAppConfig } from '../../../fetching/api/appConfig';
import { QhpWithLocationInfo } from '../../../fetching/api/liveLocations';
import { envVars } from '../../../utils/envVars';
import { ReturnComponent } from '../../../utils/reactTypes';
import {
    GoogleMapPin,
    getBadgeContentFromStopIndex,
    iconForQhpWithLocationInfo,
    popoverForQhpWithLocationInfo,
} from './helpers/GoogleMapPin';
import { DEFAULT_CENTER, DEFAULT_ZOOM, autofitMap } from './helpers/centeringAndZoom';
import { getColorFromLegendState, getColorFromPalette } from './helpers/colors';
import { useDrawRoutes } from './helpers/drawRoutes';
import { useFetchAndSnapRoutes } from './helpers/fetchAndSnapRoutes';
import { Map, Maps } from './helpers/googleMapTypes';
import { parseStops, removeNullishStops } from './helpers/parseStops';
import { NullableStop } from './helpers/stopTypes';

export type GoogleMapStopsProp = NullableStop[];
export type GoogleMapOtherProps = {
    qhpsWithLocationInfo?: QhpWithLocationInfo[];
    drawRoutes?: boolean;
    updateCenterWhenStopsChange?: boolean;
    showBadgeNumbers?: boolean;
};

// This component draws
//  1. a map.
//  2. pins for each location you pass in.
//  3. routes between the pins if applicable.
//
// The cool part is we don't erase and redraw the map, which would
//  cause flickering. Instead, we just add and remove the pins and
//  lines we need to. No flickering!
//
// We use the Google Maps Directions API, to draw road-aligned routes
//  between two points that we can draw.
//
// One trickiness is that the stops are sometimes misaligned with the
//  routes. E.g., if a stop is on top of a house, the directions go
//  over the road, so the pin for the house does not sit on top of the
//  route. This looks weird, so to solve that we "snap" the coordinates
//  of the stop to an appropriate point in the route to make things look nice.
//
// ⚠️ You should probably use <GoogleMapWithAtom/> instead of using this directly.
//
export const GoogleMap = ({
    stops: nullableStops = [],
    qhpsWithLocationInfo = [],
    drawRoutes: drawRoutesRaw = true,
    updateCenterWhenStopsChange = true,
    showBadgeNumbers = true,
}: { stops: GoogleMapStopsProp } & GoogleMapOtherProps): ReturnComponent => {
    //

    const appConfig = useAppConfig();

    // We only enable route-drawing functionality based on a permission.
    const drawRoutes = useMemo(
        () => drawRoutesRaw && appConfig.visit.draw_routes_on_visits_page,
        [drawRoutesRaw, appConfig],
    );

    // These are null now, but will eventually be set.
    const [map, setMap] = useState<Map | null>(null);
    const [maps, setMaps] = useState<Maps | null>(null);

    // Remove nullish stops.
    const stops = useMemo(() => removeNullishStops(nullableStops), [nullableStops]);

    // We only recalculate the center of the map if the caller asked us to do so.
    useEffect(() => {
        if (updateCenterWhenStopsChange) {
            autofitMap({ map, maps, latLngs: stops });
        }
    }, [map, maps, stops, updateCenterWhenStopsChange]);

    // Calculate pin and route locations.
    const [pinInfoForUnroutedStops, routes] = useMemo(() => parseStops(stops), [stops]);
    const [pinInfoForRoutedStops, directionResults] = useFetchAndSnapRoutes({
        map,
        maps,
        routes,
        drawRoutes,
    });
    const allPinInfos = useMemo(
        () => [...pinInfoForUnroutedStops, ...pinInfoForRoutedStops],
        [pinInfoForUnroutedStops, pinInfoForRoutedStops],
    );

    // Draw the lines between the pins.
    useDrawRoutes({ map, maps, directionResults, drawRoutes });

    // Memoize the icons for the QHP locations.
    // If we don't do this, then on every render, we generate a new icon component,
    //  which means if a MUI popover component anchored to that icon was open,
    //  it loses the anchor and gets displaced.
    const qhpWithLocationInfoIcons = useMemo(
        () =>
            qhpsWithLocationInfo.map((qhpWithLocationInfo) =>
                iconForQhpWithLocationInfo(qhpWithLocationInfo),
            ),
        [qhpsWithLocationInfo],
    );
    return (
        <Box
            // This is necessary to make the <GoogleMapReact/> component fill up the space.
            height={'100%'}
            width={'100%'}
        >
            {/* Draw the map. */}
            <GoogleMapReact
                bootstrapURLKeys={{
                    key: envVars.googleMapsApiKey,
                }}
                defaultZoom={DEFAULT_ZOOM}
                center={DEFAULT_CENTER}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }: { map: Map; maps: Maps }) => {
                    setMap(map);
                    setMaps(maps);
                }}
                options={(maps) => ({
                    zoomControl: true,
                    zoomControlOptions: {
                        position: maps.ControlPosition.TOP_RIGHT,
                    },
                })}
            >
                {/* Draw the pins. */}
                {allPinInfos.map((pinInfo, index) => (
                    <GoogleMapPin
                        key={`pinInfo-${index}`}
                        lat={pinInfo.stop.lat}
                        lng={pinInfo.stop.lng}
                        popoverComponent={pinInfo.stop.popoverComponent}
                        onClick={pinInfo.stop.onClick}
                        color={
                            pinInfo.stop.type === 'liveView'
                                ? getColorFromLegendState(pinInfo.stop.state)
                                : getColorFromPalette(pinInfo.groupIndex)
                        }
                        badgeContent={
                            showBadgeNumbers
                                ? getBadgeContentFromStopIndex(pinInfo.stopIndex)
                                : undefined
                        }
                        CustomPinIcon={
                            pinInfo.stop.type !== 'liveView'
                                ? pinInfo.stop.customPinIcon
                                : undefined
                        }
                    />
                ))}
                {/* Draw the live locations of en route QHPs. */}
                {qhpsWithLocationInfo.map((qhpWithLocationInfo, idx) => (
                    <GoogleMapPin
                        key={qhpWithLocationInfo.qhp.uuid}
                        lat={qhpWithLocationInfo.location_info.lat}
                        lng={qhpWithLocationInfo.location_info.lng}
                        CustomPinIcon={qhpWithLocationInfoIcons[idx]}
                        popoverComponent={popoverForQhpWithLocationInfo(qhpWithLocationInfo)}
                        // TODO: Use the colour based on which group index corresponds to the QHP UUID.
                        color={getColorFromPalette(null)}
                    />
                ))}
            </GoogleMapReact>
        </Box>
    );
};
